import { useMemo } from 'react';
import {
  priorityHint,
  roleGapScan,
  counterPick,
  compScore,
  opWarning,
} from './aiCore';

// useAIInsights —— 把"BP 实时状态 + 赛事数据 + AI 设置"
// 整合成一组结构化的 AI 推荐结果。
//
// 设计原则：
//   - 总开关 enabled = false → 全部返回 null（零计算开销）
//   - 单个功能 toggle off → 该功能返回 null
//   - 结果用 useMemo 缓存，输入不变就不重算
//
// 调用方（AIInsightPanel）只读 result，不关心计算细节。

const useAIInsights = ({ aiSettings, draft, champions, tournamentData }) => {
  const enabled = !!(aiSettings && aiSettings.isAIOn);

  // 通用输入打包
  const usedIds = draft && draft.usedChampionIds ? draft.usedChampionIds : new Set();
  const statLookup = tournamentData && tournamentData.lookup ? tournamentData.lookup : null;

  // ============== 功能 1：优先级提示 ==============
  const priority = useMemo(() => {
    if (!enabled || !aiSettings.canUse('bronze', 'priorityHint')) return null;
    if (!draft || !draft.currentStep) return null;
    return priorityHint({
      currentStep: draft.currentStep,
      champions: champions || [],
      usedIds,
      statLookup,
    });
  }, [enabled, aiSettings, draft, champions, usedIds, statLookup]);

  // ============== 功能 2：位置漏洞扫描 ==============
  const gapScan = useMemo(() => {
    if (!enabled || !aiSettings.canUse('bronze', 'roleGapScan')) return null;
    if (!draft || !draft.currentStep) return { blue: null, red: null };
    // 把 draft 的 picks 数组解出 champion 对象
    const bluePicks = (draft.blueTeam.picks || []).map((p) => p.champion);
    const redPicks  = (draft.redTeam.picks  || []).map((p) => p.champion);
    return {
      blue: roleGapScan({ teamPicks: bluePicks }),
      red:  roleGapScan({ teamPicks: redPicks }),
    };
  }, [enabled, aiSettings, draft]);

  // ============== 功能 3：Counter Pick 提示 ==============
  // 仅当 currentStep 是 pick 阶段时启用，
  // 对方该位置已经选过的英雄，作为"敌人英雄"输入
  const counter = useMemo(() => {
    if (!enabled || !aiSettings.canUse('bronze', 'counterPick')) return null;
    if (!draft || !draft.currentStep || draft.currentStep.type !== 'pick') return null;

    const myTeam = draft.currentStep.team;
    const enemyKey = myTeam === 'blue' ? 'redTeam' : 'blueTeam';
    const enemyPicks = (draft[enemyKey].picks || []).map((p) => p.champion);
    // 优先取对方相同 slot 的英雄做 counter（如果已经选过）；否则取对方已选过的最后一个
    const slot = draft.currentStep.slot;
    const enemyChampion = enemyPicks[slot] || enemyPicks.filter(Boolean).pop();
    if (!enemyChampion) return null;

    const available = (champions || []).filter((c) => !usedIds.has(c.id));
    return counterPick({
      enemyChampion,
      availableChampions: available,
      statLookup,
    });
  }, [enabled, aiSettings, draft, champions, usedIds, statLookup]);

  // ============== 功能 4：阵容综合评分 ==============
  const composition = useMemo(() => {
    if (!enabled || !aiSettings.canUse('bronze', 'compScore')) return null;
    if (!draft) return null;
    const bluePicks = (draft.blueTeam.picks || []).map((p) => p.champion);
    const redPicks  = (draft.redTeam.picks  || []).map((p) => p.champion);
    if (bluePicks.filter(Boolean).length === 0 && redPicks.filter(Boolean).length === 0) {
      return null;
    }
    return compScore({ bluePicks, redPicks });
  }, [enabled, aiSettings, draft]);

  // ============== 功能 5：OP 警告 ==============
  const opAlerts = useMemo(() => {
    if (!enabled || !aiSettings.canUse('bronze', 'opWarning')) return null;
    if (!champions || !statLookup) return null;
    return opWarning({
      champions,
      usedIds,
      statLookup,
      topN: 8,
    });
  }, [enabled, aiSettings, champions, usedIds, statLookup]);

  return {
    enabled,
    priority,
    gapScan,
    counter,
    composition,
    opAlerts,
  };
};

export default useAIInsights;
